import { useSnackbar } from 'notistack';
import { FormattedMessage, useIntl } from 'react-intl';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Box, IconButton, Link, Tooltip } from '@mui/material';
import { MouseEvent, ReactNode, useEffect, useMemo, useRef, useState } from 'react';

const LinkWithCopy = ({ href, children }: { href: string; children: ReactNode }) => {
  // other hooks
  const intl = useIntl();
  const tRef = useRef<number | null>(null);
  const { enqueueSnackbar } = useSnackbar();

  // useState hooks
  const [copied, setCopied] = useState(false);

  // dynamic variables
  const resolvedHref = useMemo(() => (href.startsWith('http') ? href : `https://${href}`), [href]);
  const tooltipTitle = copied
    ? intl.locale === 'tr'
      ? 'Kopyalandı'
      : 'Copied'
    : intl.locale === 'tr'
      ? 'Linki kopyala'
      : 'Copy link';

  // async helper functions
  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      return;
    } catch {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      ta.setSelectionRange(0, ta.value.length);
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
  };

  const handleCopy = async (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    await copyToClipboard(resolvedHref);

    setCopied(true);
    if (tRef.current) window.clearTimeout(tRef.current);
    tRef.current = window.setTimeout(() => setCopied(false), 900);

    enqueueSnackbar(<FormattedMessage id="general.linkCopied" defaultMessage="Link panoya kopyalandı" />, {
      variant: 'info',
    });
  };

  // useEffect hooks
  useEffect(() => {
    return () => {
      if (tRef.current) window.clearTimeout(tRef.current);
    };
  }, []);

  return (
    <Box
      component="span"
      sx={{
        position: 'relative',
        display: 'inline',
        userSelect: 'text',
        '& .copyBtn': {
          position: 'absolute',
          left: '100%',
          marginLeft: 4,
          top: '50%',
          transform: 'translateY(-50%)',
          opacity: 1,
          transition: 'opacity 120ms ease',
          userSelect: 'none',
          '@media (hover: hover) and (pointer: fine)': {
            opacity: 0,
          },
        },
        '&:hover .copyBtn, &:focus-within .copyBtn': { opacity: 1 },
      }}>
      <Link
        href={resolvedHref}
        target="_blank"
        rel="noopener noreferrer"
        underline="hover"
        sx={{ color: 'inherit', textDecorationColor: 'inherit' }}>
        {children}
      </Link>

      <Tooltip title={tooltipTitle} arrow>
        <IconButton
          className="copyBtn"
          size="small"
          aria-label="copy link"
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onClick={handleCopy}>
          <ContentCopyIcon fontSize="inherit" />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

export default LinkWithCopy;
